import { Link } from 'react-router-dom';
import type { Property } from '../../../lib/supabase';
import LazyImg from '../../../components/base/LazyImg';
import { useMobileTheme } from '../../../hooks/useMobileTheme';
import { trimToDistrict } from '../../../lib/locationUtils';

interface MobilePropertiesSectionProps {
  properties: Property[];
  loading: boolean;
  onPublish?: () => void;
}

const PLACEHOLDER_IMAGES = [
  'https://public.readdy.ai/ai/img_res/04d86ac4e8b2bc4bebf646b2da0faef6.jpg',
  'https://readdy.ai/api/search-image?query=cozy%20living%20room%20apartment%20warm%20lighting%20comfortable%20furniture%20clean%20simple%20interior%20professional%20real%20estate%20photo%20neutral%20background&width=600&height=400&seq=mlp2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=contemporary%20apartment%20bedroom%20natural%20light%20clean%20design%20simple%20furniture%20white%20walls%20professional%20photography&width=600&height=400&seq=mlp3&orientation=landscape',
  'https://readdy.ai/api/search-image?query=spacious%20modern%20dining%20room%20apartment%20clean%20minimal%20design%20warm%20lighting%20neutral%20colors%20professional%20interior%20photography&width=600&height=400&seq=mlp4&orientation=landscape',
];

function getPlaceholder(id: string) {
  return PLACEHOLDER_IMAGES[id.charCodeAt(0) % PLACEHOLDER_IMAGES.length];
}

export default function MobilePropertiesSection({ properties, loading, onPublish }: MobilePropertiesSectionProps) {
  const { theme } = useMobileTheme();

  const accentColor = theme === 'pink' ? 'text-rose-500' : 'text-teal-600';
  const badgeBg = theme === 'pink'
    ? 'bg-rose-50 border-rose-100 text-rose-500'
    : 'bg-teal-50 border-teal-100 text-teal-600';
  const dotColor = theme === 'pink' ? 'bg-rose-400' : 'bg-teal-400';
  const btnBg = theme === 'pink'
    ? 'bg-rose-400 hover:bg-rose-500'
    : theme === 'white'
    ? 'bg-teal-700 hover:bg-teal-800'
    : 'bg-teal-500 hover:bg-teal-600';
  const priceBadgeBg = theme === 'pink' ? 'bg-rose-500' : 'bg-teal-500';
  const tagBg = theme === 'pink' ? 'bg-rose-400/90' : 'bg-teal-500/90';
  const cardRing = theme === 'pink' ? 'hover:ring-rose-200' : 'hover:ring-teal-200';

  return (
    <section className="lg:hidden bg-gray-50 py-7 px-4">

      {/* ── 區塊標題 ── */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold mb-1.5 ${badgeBg}`}>
            <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${dotColor}`}></span>
            即時上架房源
          </div>
          <h2 className="text-xl font-black text-gray-900 leading-tight">零仲介費好房源</h2>
          <p className="text-[11px] text-gray-400 mt-0.5">以下均為平台實際刊登，非廣告</p>
        </div>
        <Link to="/regions" className={`flex items-center gap-1 text-xs font-semibold whitespace-nowrap ${accentColor}`}>
          全部 <i className="ri-arrow-right-line text-xs"></i>
        </Link>
      </div>

      {/* ── 信任三欄 ── */}
      <div className="flex items-center justify-around bg-white rounded-2xl px-3 py-2.5 border border-gray-100 mb-4">
        <div className="flex flex-col items-center gap-1">
          <i className="ri-shield-check-fill text-teal-500 text-lg"></i>
          <span className="text-[10px] text-gray-500 font-medium">AI 審核</span>
        </div>
        <div className="w-px h-8 bg-gray-100"></div>
        <div className="flex flex-col items-center gap-1">
          <i className="ri-money-dollar-circle-line text-teal-500 text-lg"></i>
          <span className="text-[10px] text-gray-500 font-medium">零仲介費</span>
        </div>
        <div className="w-px h-8 bg-gray-100"></div>
        <div className="flex flex-col items-center gap-1">
          <i className="ri-user-line text-teal-500 text-lg"></i>
          <span className="text-[10px] text-gray-500 font-medium">屋主直聯</span>
        </div>
        <div className="w-px h-8 bg-gray-100"></div>
        <div className="flex flex-col items-center gap-1">
          <i className="ri-time-line text-teal-500 text-lg"></i>
          <span className="text-[10px] text-gray-500 font-medium">即時更新</span>
        </div>
      </div>

      {/* ── 載入中 ── */}
      {loading && (
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
              <div className="h-36 bg-gray-100"></div>
              <div className="p-3 space-y-2">
                <div className="h-3 bg-gray-100 rounded-full w-4/5"></div>
                <div className="h-2.5 bg-gray-100 rounded-full w-3/5"></div>
                <div className="h-4 bg-gray-100 rounded-full w-2/5"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── 有房源 ── */}
      {!loading && properties.length > 0 && (
        <>
          {/* 2欄 Grid 卡片 */}
          <div className="grid grid-cols-2 gap-3">
            {properties.slice(0, 4).map((property) => {
              const img = property.photos?.[0] ?? getPlaceholder(property.id);
              const pos = property.photo_positions?.[0] ?? '50% 50%';
              const zoom = property.photo_zooms?.[0] ?? 1;
              return (
                <Link
                  key={property.id}
                  to={`/property/${property.id}`}
                  className={`group bg-white rounded-2xl overflow-hidden border border-gray-100 ring-2 ring-transparent ${cardRing} transition-all duration-200 cursor-pointer active:scale-[0.98]`}
                  style={{ touchAction: 'manipulation' }}
                >
                  {/* 縮圖 */}
                  <div className="relative w-full h-36 overflow-hidden bg-gray-100">
                    <LazyImg
                      src={img}
                      alt={property.title}
                      containerClassName="w-full h-full"
                      imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      imgStyle={{ objectPosition: pos }}
                    />
                    {/* 直租標籤 */}
                    <span className={`absolute top-2 left-2 px-1.5 py-0.5 text-white text-[9px] font-bold rounded-full whitespace-nowrap ${tagBg}`}>
                      零仲介費
                    </span>
                    {/* 瀏覽數 */}
                    {(property.view_count ?? 0) > 0 && (
                      <span className="absolute bottom-2 right-2 flex items-center gap-0.5 px-1.5 py-0.5 bg-black/45 text-white text-[9px] rounded-full whitespace-nowrap">
                        <i className="ri-eye-line text-[9px]"></i>
                        {property.view_count}
                      </span>
                    )}
                  </div>

                  {/* 文字資訊 */}
                  <div className="p-2.5">
                    <p className="font-bold text-gray-900 text-xs leading-snug line-clamp-2 mb-1.5 min-h-[2.4rem]">
                      {property.title}
                    </p>
                    <p className="text-[10px] text-gray-400 flex items-center gap-0.5 mb-2 truncate">
                      <i className="ri-map-pin-line text-teal-400 flex-shrink-0 text-[10px]"></i>
                      <span className="truncate">{trimToDistrict(property.location)}</span>
                    </p>

                    {/* 房型坪數標籤 */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      <span className="text-[9px] bg-gray-50 text-gray-500 px-1.5 py-0.5 rounded-md border border-gray-100">
                        {property.area}坪
                      </span>
                      {!['店面', '廠房', '辦公室'].includes(property.property_type || '') && (
                        <span className="text-[9px] bg-gray-50 text-gray-500 px-1.5 py-0.5 rounded-md border border-gray-100">
                          {property.rooms}房{property.bathrooms}衛
                        </span>
                      )}
                    </div>

                    {/* 租金 */}
                    <div className={`inline-flex items-baseline gap-0.5 px-2 py-1 rounded-lg text-white ${priceBadgeBg}`}>
                      <span className="text-xs font-black">
                        {property.price.toLocaleString()}
                      </span>
                      <span className="text-[9px] font-medium opacity-80">/月</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* 查看更多 */}
          <Link
            to="/regions"
            className={`mt-4 w-full flex items-center justify-center gap-2 py-3.5 ${btnBg} text-white rounded-2xl font-bold text-sm cursor-pointer whitespace-nowrap transition-colors`}
            style={{ touchAction: 'manipulation' }}
          >
            <i className="ri-search-line text-base"></i>
            搜尋更多六都房源
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </>
      )}

      {/* ── 無房源 ── */}
      {!loading && properties.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 bg-white rounded-2xl border border-gray-100">
          <div className="w-14 h-14 flex items-center justify-center bg-teal-50 rounded-full mb-3">
            <i className="ri-home-4-line text-2xl text-teal-400"></i>
          </div>
          <p className="font-semibold text-gray-600 mb-1 text-sm">成為第一個發布房源的屋主</p>
          <p className="text-xs text-gray-400 mb-5 text-center px-4">完全免費，AI 輔助撰稿，零仲介費</p>
          {onPublish ? (
            <button
              type="button"
              onClick={onPublish}
              className="px-6 py-2.5 bg-teal-500 text-white rounded-full font-semibold text-sm cursor-pointer whitespace-nowrap"
            >
              立即發布房源
            </button>
          ) : (
            <Link to="/publish" className="px-6 py-2.5 bg-teal-500 text-white rounded-full font-semibold text-sm cursor-pointer whitespace-nowrap">
              立即發布房源
            </Link>
          )}
        </div>
      )}

      {/* ── 底部信任列 ── */}
      <div className="mt-4 flex items-center justify-center gap-3 text-[10px] text-gray-400">
        <span className="flex items-center gap-1">
          <i className="ri-lock-line text-[10px]"></i>資訊安全
        </span>
        <span className="text-gray-200">·</span>
        <span className="flex items-center gap-1">
          <i className="ri-customer-service-2-line text-[10px]"></i>客服支援
        </span>
        <span className="text-gray-200">·</span>
        <span className="flex items-center gap-1">
          <i className="ri-map-pin-2-line text-[10px]"></i>六都覆蓋
        </span>
      </div>
    </section>
  );
}
